/**
 * Shared contracts between the web frontend and the API.
 *
 * Everything in this file is isomorphic: it runs in Node (Express route
 * validation) and in the browser (form validation / API response typing).
 */
import { z } from 'zod';
import { validateCreateRequirementRuleConfig } from './requirementRuleConfig.js';
// ─── Health ──────────────────────────────────────────────────────────────
export const HealthStatus = z.enum(['ok', 'degraded']);
export const HealthResponseSchema = z.object({
    status: HealthStatus,
    uptimeSeconds: z.number().int().nonnegative(),
    timestamp: z.string(),
    version: z.string(),
    checks: z.object({
        database: z.object({
            status: HealthStatus,
            latencyMs: z.number().nonnegative().nullable(),
            error: z.string().nullable(),
        }),
    }),
});
export const LivenessResponseSchema = z.object({
    status: z.literal('ok'),
    uptimeSeconds: z.number().int().nonnegative(),
    timestamp: z.string(),
});
export const WorkerHealthState = z.enum(['healthy', 'stale', 'none']);
export const WorkerInstanceHealthSchema = z.object({
    workerId: z.string(),
    state: WorkerHealthState,
    lastSeenAt: z.string().nullable(),
    startedAt: z.string().nullable(),
    version: z.string().nullable(),
    staleSinceMs: z.number().int().nonnegative().nullable(),
});
export const ReadinessResponseSchema = z.object({
    status: HealthStatus,
    timestamp: z.string(),
    checks: z.object({
        database: z.object({
            status: HealthStatus,
            latencyMs: z.number().int().nonnegative().nullable(),
            error: z.string().nullable(),
        }),
        workers: z.object({
            status: HealthStatus,
            summary: z.enum(['healthy', 'stale', 'none']),
            instances: z.array(WorkerInstanceHealthSchema),
            freshnessThresholdMs: z.number().int().positive(),
        }),
    }),
});
export const AlertSeverity = z.enum(['critical', 'warning', 'info']);
export const AlertableConditionSchema = z.object({
    code: z.string(),
    severity: AlertSeverity,
    active: z.boolean(),
    message: z.string(),
});
export const AlertsResponseSchema = z.object({
    status: HealthStatus,
    timestamp: z.string(),
    alerts: z.array(AlertableConditionSchema),
});
export { CORRELATION_ID_HEADER, sanitizeCorrelationId, } from './observability.js';
// ─── Common primitives ───────────────────────────────────────────────────
export const Uuid = z.string().uuid();
const trimmedName = (max, label = 'Name') => z.string().trim().min(1, `${label} is required`).max(max, `${label} must be at most ${max} characters`);
export const LifecycleStatus = z.enum(['active', 'inactive']);
/** ISO calendar date (YYYY-MM-DD) — enrollment/plan dates have no timezone semantics. */
export const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected date as YYYY-MM-DD');
function isValidCalendarDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match)
        return false;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const probe = new Date(Date.UTC(year, month - 1, day));
    return (probe.getUTCFullYear() === year &&
        probe.getUTCMonth() === month - 1 &&
        probe.getUTCDate() === day);
}
/** Calendar-valid ISO date (rejects impossible dates such as 2026-02-31). */
export const CalendarIsoDate = IsoDate.refine(isValidCalendarDate, {
    message: 'Invalid calendar date',
});
export const PaginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    search: z.string().trim().max(200).optional(),
});
// ─── Roles & memberships ─────────────────────────────────────────────────
export const MembershipRole = z.enum(['STUDENT', 'INSTRUCTOR', 'PROGRAM_DIRECTOR']);
export const MembershipStatus = z.enum(['active', 'inactive', 'pending']);
/** Placeholder user id for pending member invitations (no login user exists yet). */
export const PENDING_MEMBER_USER_ID = '00000000-0000-0000-0000-000000000001';
const AuthUserBaseSchema = z.object({
    id: Uuid,
    email: z.string().email(),
    fullName: z.string(),
});
/** Tenant user: role and program context come from active memberships only. */
export const TenantAuthUserSchema = AuthUserBaseSchema.extend({
    authKind: z.literal('tenant'),
    role: MembershipRole,
    programId: Uuid,
    organizationId: Uuid,
    organizationName: z.string().nullable(),
    programName: z.string().nullable(),
    cohortId: Uuid.nullable(),
    cohortName: z.string().nullable(),
    /** Active platform_admins row; evaluated separately from tenant role. */
    platformAdminActive: z.boolean(),
});
/** Authenticated platform operator without tenant membership context on this session. */
export const PlatformAuthUserSchema = AuthUserBaseSchema.extend({
    authKind: z.literal('platform'),
});
/** Signed-in user with no active tenant membership and no platform authority. */
export const SessionOnlyAuthUserSchema = AuthUserBaseSchema.extend({
    authKind: z.literal('session_only'),
});
export const AuthUserSchema = z.discriminatedUnion('authKind', [
    TenantAuthUserSchema,
    PlatformAuthUserSchema,
    SessionOnlyAuthUserSchema,
]);
export const LoginInputSchema = z
    .object({
    email: z.string().trim().toLowerCase().email('A valid email is required').max(320),
    password: z.string().min(8, 'Password must be at least 8 characters').max(200),
})
    .strict();
export const DemoRequestInterestSchema = z.enum([
    'REQUEST_DEMO',
    'PRICING',
    'MORE_INFORMATION',
    'OTHER',
]);
export const DEMO_REQUEST_INTEREST_LABELS = {
    REQUEST_DEMO: 'Request a Demo',
    PRICING: 'Pricing',
    MORE_INFORMATION: 'More Information',
    OTHER: 'Other',
};
/** Disallows line breaks and other control characters in single-line demo form fields. */
const DEMO_REQUEST_SINGLE_LINE_INVALID = /[\u0000-\u001F\u007F]/;
/** Disallows control characters in multi-line message, but allows CR/LF. */
const DEMO_REQUEST_MESSAGE_INVALID_CONTROL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/;
function demoRequestSingleLineField(max, options) {
    let schema = z.string().trim();
    if (options.required) {
        schema = schema.min(1, options.requiredMessage);
    }
    return schema
        .max(max)
        .refine((value) => value === '' || !DEMO_REQUEST_SINGLE_LINE_INVALID.test(value), {
        message: 'This field cannot contain line breaks or control characters',
    });
}
export const DemoRequestInputSchema = z
    .object({
    fullName: demoRequestSingleLineField(120, {
        required: true,
        requiredMessage: 'Full name is required',
    }),
    institution: demoRequestSingleLineField(200, {
        required: true,
        requiredMessage: 'School or institution is required',
    }),
    workEmail: z
        .string()
        .trim()
        .transform((value) => value.toLowerCase())
        .pipe(demoRequestSingleLineField(320, {
        required: true,
        requiredMessage: 'A valid work email is required',
    }).pipe(z.string().email('A valid work email is required'))),
    phone: demoRequestSingleLineField(40, { required: false })
        .optional()
        .transform((v) => (v === '' || v === undefined ? undefined : v)),
    jobTitle: demoRequestSingleLineField(120, { required: false })
        .optional()
        .transform((v) => (v === '' || v === undefined ? undefined : v)),
    program: demoRequestSingleLineField(120, { required: false })
        .optional()
        .transform((v) => (v === '' || v === undefined ? undefined : v)),
    interest: DemoRequestInterestSchema,
    message: z
        .string()
        .trim()
        .max(4000)
        .refine((value) => value === '' || !DEMO_REQUEST_MESSAGE_INVALID_CONTROL.test(value), {
        message: 'Message contains invalid control characters',
    })
        .optional()
        .transform((v) => (v === '' ? undefined : v)),
    companyWebsite: z
        .string()
        .max(200)
        .optional()
        .transform((v) => v ?? ''),
})
    .strict();
export const DemoRequestSuccessResponseSchema = z.object({
    ok: z.literal(true),
});
export const TenantProgramContextInputSchema = z
    .object({
    programId: Uuid,
})
    .strict();
export const TenantProgramContextSchema = z.object({
    programId: Uuid,
    programName: z.string().nullable(),
    organizationId: Uuid,
    organizationName: z.string().nullable(),
    role: MembershipRole,
});
// ─── Organizations / programs / cohorts ───────────────────────────────────
export const OrganizationSchema = z.object({
    id: Uuid,
    name: z.string(),
    slug: z.string(),
    status: LifecycleStatus,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const ProgramSchema = z.object({
    id: Uuid,
    organizationId: Uuid,
    name: z.string(),
    slug: z.string(),
    status: LifecycleStatus,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const UpdateProgramInputSchema = z
    .object({
    name: trimmedName(200, 'Program name').optional(),
})
    .strict();
export const CohortStatus = z.enum(['planned', 'active', 'completed', 'archived']);
export const CohortSchema = z.object({
    id: Uuid,
    programId: Uuid,
    name: z.string(),
    code: z.string(),
    status: CohortStatus,
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateCohortInputSchema = z
    .object({
    programId: Uuid,
    name: trimmedName(200, 'Cohort name'),
    code: z
        .string()
        .trim()
        .min(1, 'Cohort code is required')
        .max(40)
        .regex(/^[A-Za-z0-9][A-Za-z0-9\-_]*$/, 'Code may contain letters, numbers, dashes and underscores'),
    startDate: IsoDate.nullable().optional(),
    endDate: IsoDate.nullable().optional(),
})
    .strict()
    .refine((v) => !v.startDate || !v.endDate || v.startDate <= v.endDate, {
    message: 'End date must be on or after start date',
    path: ['endDate'],
});
export const UpdateCohortInputSchema = z
    .object({
    name: trimmedName(200, 'Cohort name').optional(),
    status: CohortStatus.optional(),
    startDate: IsoDate.nullable().optional(),
    endDate: IsoDate.nullable().optional(),
})
    .strict()
    .refine((v) => !v.startDate || !v.endDate || v.startDate <= v.endDate, {
    message: 'End date must be on or after start date',
    path: ['endDate'],
});
export const ListCohortsQuerySchema = PaginationQuerySchema.extend({
    programId: Uuid.optional(),
    status: CohortStatus.optional(),
});
export const CohortListResponseSchema = z.object({
    data: z.array(CohortSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
});
// ─── Clinical sites ──────────────────────────────────────────────────────
export const ClinicalSiteSchema = z.object({
    id: Uuid,
    programId: Uuid,
    name: z.string(),
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    postalCode: z.string().nullable(),
    status: LifecycleStatus,
    notes: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v === '' || v === undefined ? null : v ?? null));
export const CreateClinicalSiteInputSchema = z
    .object({
    programId: Uuid,
    name: trimmedName(200, 'Site name'),
    addressLine1: optionalText(200),
    addressLine2: optionalText(200),
    city: optionalText(100),
    state: optionalText(100),
    postalCode: optionalText(20),
    notes: optionalText(2000),
})
    .strict();
export const UpdateClinicalSiteInputSchema = z
    .object({
    name: trimmedName(200, 'Site name').optional(),
    addressLine1: optionalText(200),
    addressLine2: optionalText(200),
    city: optionalText(100),
    state: optionalText(100),
    postalCode: optionalText(20),
    notes: optionalText(2000),
    status: LifecycleStatus.optional(),
})
    .strict();
export const ListClinicalSitesQuerySchema = PaginationQuerySchema.extend({
    programId: Uuid.optional(),
    status: LifecycleStatus.optional(),
});
export const ClinicalSiteListResponseSchema = z.object({
    data: z.array(ClinicalSiteSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
});
// ─── Specialties & aliases ───────────────────────────────────────────────
export const SpecialtySchema = z.object({
    id: Uuid,
    programId: Uuid,
    name: z.string(),
    code: z.string(),
    status: LifecycleStatus,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateSpecialtyInputSchema = z
    .object({
    programId: Uuid,
    name: trimmedName(200, 'Specialty name'),
    code: z
        .string()
        .trim()
        .toUpperCase()
        .min(1, 'Specialty code is required')
        .max(20)
        .regex(/^[A-Z0-9][A-Z0-9\-_]*$/, 'Code may contain letters, numbers, dashes and underscores'),
})
    .strict();
export const UpdateSpecialtyInputSchema = z
    .object({
    name: trimmedName(200, 'Specialty name').optional(),
    status: LifecycleStatus.optional(),
})
    .strict();
export const SpecialtyAliasSchema = z.object({
    id: Uuid,
    specialtyId: Uuid,
    alias: z.string(),
    createdAt: z.string(),
});
const aliasInput = z
    .string()
    .trim()
    .min(1, 'Alias is required')
    .max(100)
    .transform((v) => v.toLowerCase());
export const CreateSpecialtyAliasInputSchema = z.object({ alias: aliasInput }).strict();
export const ListSpecialtiesQuerySchema = PaginationQuerySchema.extend({
    programId: Uuid.optional(),
    status: LifecycleStatus.optional(),
});
export const SpecialtyListResponseSchema = z.object({
    data: z.array(SpecialtySchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
});
// ─── Clinical roles ──────────────────────────────────────────────────────
export const ClinicalRoleSchema = z.object({
    id: Uuid,
    programId: Uuid,
    code: z.string(),
    displayName: z.string(),
    sortOrder: z.number().int().nonnegative(),
    status: LifecycleStatus,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateClinicalRoleInputSchema = z
    .object({
    programId: Uuid,
    code: z
        .string()
        .trim()
        .toUpperCase()
        .min(1, 'Role code is required')
        .max(40)
        .regex(/^[A-Z0-9][A-Z0-9\-_]*$/, 'Code may contain letters, numbers, dashes and underscores'),
    displayName: trimmedName(100, 'Display name'),
    sortOrder: z.coerce.number().int().min(0).max(999).default(0),
})
    .strict();
export const UpdateClinicalRoleInputSchema = z
    .object({
    displayName: trimmedName(100, 'Display name').optional(),
    sortOrder: z.coerce.number().int().min(0).max(999).optional(),
    status: LifecycleStatus.optional(),
})
    .strict();
export const ListClinicalRolesQuerySchema = PaginationQuerySchema.extend({
    programId: Uuid.optional(),
    status: LifecycleStatus.optional(),
});
export const ClinicalRoleListResponseSchema = z.object({
    data: z.array(ClinicalRoleSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
});
// ─── Procedures & aliases ────────────────────────────────────────────────
export const ProcedureSchema = z.object({
    id: Uuid,
    programId: Uuid,
    canonicalName: z.string(),
    defaultSpecialtyId: Uuid.nullable(),
    status: LifecycleStatus,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateProcedureInputSchema = z
    .object({
    programId: Uuid,
    canonicalName: trimmedName(300, 'Procedure name'),
    defaultSpecialtyId: Uuid.nullable().optional(),
})
    .strict();
export const UpdateProcedureInputSchema = z
    .object({
    canonicalName: trimmedName(300, 'Procedure name').optional(),
    defaultSpecialtyId: Uuid.nullable().optional(),
    status: LifecycleStatus.optional(),
})
    .strict();
export const ProcedureAliasSchema = z.object({
    id: Uuid,
    procedureId: Uuid,
    alias: z.string(),
    createdAt: z.string(),
});
export const CreateProcedureAliasInputSchema = z.object({ alias: aliasInput }).strict();
export const ListProceduresQuerySchema = PaginationQuerySchema.extend({
    programId: Uuid.optional(),
    specialtyId: Uuid.optional(),
    status: LifecycleStatus.optional(),
});
export const ProcedureListResponseSchema = z.object({
    data: z.array(ProcedureSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
});
// ─── Requirement engine foundation ───────────────────────────────────────
export const RequirementRuleType = z.enum([
    'TOTAL_MINIMUM',
    'CATEGORY_MINIMUM',
    'CATEGORY_MAXIMUM',
    'ROLE_MINIMUM',
    'ROLE_MAXIMUM',
    'CATEGORY_ROLE_MINIMUM',
    'CATEGORY_ROLE_MAXIMUM',
    'DISTINCT_CATEGORY_MINIMUM',
    'SPECIALTY_SET_MINIMUM',
    'SPECIALTY_SET_ROLE_MINIMUM',
    'DISTINCT_SPECIALTY_THRESHOLD_MINIMUM',
    'COUNT_CAP',
    'EXCLUSION',
    'CONDITIONAL_RULE',
    'HOURS_MINIMUM',
]);
export { DistinctSpecialtyThresholdMinimumConfigSchema, RequirementRuleContributionCapSchema, SpecialtySetMinimumConfigSchema, SpecialtySetRoleMinimumConfigSchema, TotalMinimumConfigSchema, parseRequirementRuleConfig, validateCreateRequirementRuleConfig, } from './requirementRuleConfig.js';
export const RequirementPlanStatus = z.enum(['draft', 'active', 'retired']);
export const RequirementTemplateSchema = z.object({
    id: Uuid,
    programId: Uuid.nullable(),
    name: z.string(),
    description: z.string().nullable(),
    status: LifecycleStatus,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const ListRequirementTemplatesQuerySchema = z.object({
    programId: Uuid,
});
export const RequirementTemplateListResponseSchema = z.object({
    data: z.array(RequirementTemplateSchema),
});
export const RequirementPlanSchema = z.object({
    id: Uuid,
    programId: Uuid,
    name: z.string(),
    description: z.string().nullable(),
    currentVersionId: Uuid.nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const RequirementPlanVersionSchema = z.object({
    id: Uuid,
    planId: Uuid,
    versionNumber: z.number().int().positive(),
    status: RequirementPlanStatus,
    isSample: z.boolean(),
    activatedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const RequirementRuleSchema = z.object({
    id: Uuid,
    versionId: Uuid,
    ruleType: RequirementRuleType,
    clinicalRoleId: Uuid.nullable(),
    specialtyId: Uuid.nullable(),
    threshold: z.number().int().nonnegative().nullable(),
    config: z.record(z.string(), z.unknown()).nullable(),
    notes: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CohortRequirementAssignmentSchema = z.object({
    id: Uuid,
    cohortId: Uuid,
    versionId: Uuid,
    effectiveFrom: z.string(),
    effectiveTo: z.string().nullable(),
    assignedAt: z.string(),
    assignedBy: Uuid.nullable(),
    createdAt: z.string(),
});
export const CreateRequirementPlanInputSchema = z
    .object({
    programId: Uuid,
    name: trimmedName(200, 'Plan name'),
    description: optionalText(2000),
    templateId: Uuid.nullable().optional(),
})
    .strict();
export const UpdateRequirementPlanInputSchema = z
    .object({
    name: trimmedName(200, 'Plan name').optional(),
    description: optionalText(2000),
})
    .strict();
export const CreateRequirementRuleInputSchema = z
    .object({
    ruleType: RequirementRuleType,
    specialtyId: Uuid.nullable().optional(),
    clinicalRoleId: Uuid.nullable().optional(),
    threshold: z.coerce.number().int().min(0).max(10000).nullable().optional(),
    config: z.record(z.string(), z.unknown()).nullable().optional(),
    notes: optionalText(2000),
})
    .strict()
    .superRefine((input, ctx) => {
    const result = validateCreateRequirementRuleConfig(input.ruleType, input.config ?? null);
    if (!result.ok) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.message, path: ['config'] });
    }
});
export const ListRequirementPlansQuerySchema = PaginationQuerySchema.extend({
    programId: Uuid.optional(),
});
export const RequirementPlanListResponseSchema = z.object({
    data: z.array(RequirementPlanSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
});
export const RequirementPlanDetailSchema = z.object({
    plan: RequirementPlanSchema,
    versions: z.array(RequirementPlanVersionSchema),
    activeVersion: RequirementPlanVersionSchema.nullable(),
    rules: z.array(RequirementRuleSchema),
});
// ─── Students / enrollments ──────────────────────────────────────────────
export const EnrollmentStatus = z.enum(['planned', 'active', 'completed', 'withdrawn']);
export const StudentProfileSchema = z.object({
    id: Uuid,
    userId: Uuid,
    studentIdNumber: z.string().nullable(),
    programId: Uuid,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CohortEnrollmentSchema = z.object({
    id: Uuid,
    studentProfileId: Uuid,
    cohortId: Uuid,
    status: EnrollmentStatus,
    startedAt: z.string().nullable(),
    endedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const StudentSummarySchema = z.object({
    id: Uuid,
    userId: Uuid,
    fullName: z.string(),
    email: z.string(),
    studentIdNumber: z.string().nullable(),
    programId: Uuid,
    cohortId: Uuid.nullable(),
    cohortName: z.string().nullable(),
    enrollmentStatus: EnrollmentStatus.nullable(),
    createdAt: z.string(),
});
export const ListStudentsQuerySchema = PaginationQuerySchema.extend({
    programId: Uuid.optional(),
    cohortId: Uuid.optional(),
});
export const StudentListResponseSchema = z.object({
    data: z.array(StudentSummarySchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
});
export const CreateEnrollmentInputSchema = z
    .object({
    studentProfileId: Uuid,
    cohortId: Uuid,
    status: EnrollmentStatus.default('planned'),
    startedAt: IsoDate.nullable().optional(),
    endedAt: IsoDate.nullable().optional(),
})
    .strict();
// ─── Members (instructors / PDs) ─────────────────────────────────────────
export const MemberSummarySchema = z.object({
    membershipId: Uuid,
    userId: Uuid,
    fullName: z.string(),
    email: z.string(),
    role: MembershipRole,
    status: MembershipStatus,
    createdAt: z.string(),
});
export const ListMembersQuerySchema = PaginationQuerySchema.extend({
    programId: Uuid.optional(),
    role: MembershipRole.optional(),
});
export const MemberListResponseSchema = z.object({
    data: z.array(MemberSummarySchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
});
/** Shared password bounds for account provisioning (members, students, production bootstrap). */
export const provisionPassword = z.string().min(8, 'Password must be at least 8 characters').max(200);
/** Shared email normalization for account provisioning. */
export const provisionEmail = z.string().trim().toLowerCase().email('A valid email is required').max(320);
export const CreateMemberInputSchema = z
    .object({
    programId: Uuid,
    email: provisionEmail,
    password: provisionPassword,
    fullName: trimmedName(200, 'Full name'),
    role: z.enum(['INSTRUCTOR', 'PROGRAM_DIRECTOR']),
})
    .strict();
export const UpdateMemberInputSchema = z
    .object({
    status: MembershipStatus.optional(),
})
    .strict();
// ─── Platform admin API (Phase 5G-B) ─────────────────────────────────────
export const ListPlatformOrganizationsQuerySchema = PaginationQuerySchema;
export const ListPlatformUsersQuerySchema = PaginationQuerySchema;
export const ListPlatformOrganizationProgramsQuerySchema = PaginationQuerySchema.omit({ search: true });
export const ListPlatformProgramMembershipsQuerySchema = PaginationQuerySchema.omit({ search: true });
export const ListPlatformUserMembershipsQuerySchema = PaginationQuerySchema.omit({ search: true });
export const CreatePlatformOrganizationInputSchema = z
    .object({
    name: trimmedName(200, 'Organization name'),
})
    .strict();
export const PlatformLifecycleStatusInputSchema = z
    .object({
    status: LifecycleStatus,
})
    .strict();
export const CreatePlatformProgramInputSchema = z
    .object({
    name: trimmedName(200, 'Program name'),
})
    .strict();
export const PlatformProgramDirectorInputSchema = z
    .object({
    fullName: trimmedName(200, 'Full name'),
    email: provisionEmail,
})
    .strict();
export const PlatformUserStatusInputSchema = z
    .object({
    status: z.enum(['active', 'inactive']),
})
    .strict();
export const OrganizationOnboardingSummarySchema = z.object({
    hasActiveProgram: z.boolean(),
    hasActiveProgramDirector: z.boolean(),
    hasInstructors: z.boolean(),
    hasStudents: z.boolean(),
    hasRequirementPlan: z.boolean(),
    hasClinicalSites: z.boolean(),
    hasSpecialtiesOrProcedures: z.boolean(),
});
export const PlatformOrganizationDetailSchema = OrganizationSchema.extend({
    onboarding: OrganizationOnboardingSummarySchema,
});
export const PlatformOverviewSchema = z.object({
    organizations: z.object({
        total: z.number().int().nonnegative(),
        active: z.number().int().nonnegative(),
        inactive: z.number().int().nonnegative(),
    }),
    programs: z.object({
        total: z.number().int().nonnegative(),
        active: z.number().int().nonnegative(),
        inactive: z.number().int().nonnegative(),
    }),
    users: z.object({
        active: z.number().int().nonnegative(),
        membershipsByRole: z.record(z.string(), z.number().int().nonnegative()),
    }),
    platformAdmins: z.object({
        total: z.number().int().nonnegative(),
        active: z.number().int().nonnegative(),
    }),
    workers: z.object({
        status: z.string(),
        summary: z.string(),
        instanceCount: z.number().int().nonnegative(),
        freshnessThresholdMs: z.number().int().nonnegative(),
    }),
    application: z.object({
        status: z.string(),
        database: z.object({
            status: z.string(),
        }),
    }),
});
export const PlatformUserSummarySchema = z.object({
    id: Uuid,
    email: z.string().email(),
    fullName: z.string(),
    status: z.enum(['active', 'inactive']),
    createdAt: z.string(),
});
export const PlatformMembershipSummarySchema = z.object({
    membershipId: Uuid,
    organizationId: Uuid,
    organizationName: z.string(),
    organizationStatus: LifecycleStatus,
    programId: Uuid.nullable(),
    programName: z.string().nullable(),
    programStatus: LifecycleStatus.nullable(),
    role: MembershipRole,
    status: MembershipStatus,
    createdAt: z.string(),
});
export const PlatformUserDetailSchema = PlatformUserSummarySchema.extend({
    updatedAt: z.string(),
    memberships: z.array(PlatformMembershipSummarySchema),
    membershipsTotal: z.number().int().nonnegative(),
    membershipsPageSize: z.number().int().nonnegative(),
    platformAdmin: z
        .object({
        status: z.enum(['active', 'inactive']),
    })
        .nullable(),
});
export const PaginatedListResponseSchema = z.object({
    data: z.array(z.unknown()),
    page: z.number().int(),
    pageSize: z.number().int(),
    total: z.number().int(),
    totalPages: z.number().int(),
});
export const CreateStudentInputSchema = z
    .object({
    programId: Uuid,
    email: provisionEmail,
    password: provisionPassword,
    fullName: trimmedName(200, 'Full name'),
    studentIdNumber: z.string().trim().max(60).optional().transform((v) => (v?.trim() ? v.trim() : null)),
    cohortId: Uuid.optional(),
})
    .strict();
export const EnrollStudentInputSchema = z
    .object({
    cohortId: Uuid,
    status: EnrollmentStatus.default('planned'),
    startedAt: IsoDate.nullable().optional(),
    endedAt: IsoDate.nullable().optional(),
})
    .strict();
// ─── Dashboards ──────────────────────────────────────────────────────────
export const ProgramDirectorDashboardSchema = z.object({
    programId: Uuid,
    programName: z.string(),
    organizationName: z.string(),
    counts: z.object({
        activeCohorts: z.number().int().nonnegative(),
        students: z.number().int().nonnegative(),
        instructors: z.number().int().nonnegative(),
        activeClinicalSites: z.number().int().nonnegative(),
        activeSpecialties: z.number().int().nonnegative(),
        procedures: z.number().int().nonnegative(),
        requirementPlans: z.number().int().nonnegative(),
    }),
    recentAuditEvents: z.array(z.object({
        id: Uuid,
        eventType: z.string(),
        targetType: z.string(),
        occurredAt: z.string(),
    })),
});
export const InstructorDashboardSchema = z.object({
    programId: Uuid,
    programName: z.string(),
    organizationName: z.string(),
    cohorts: z.array(z.object({
        id: Uuid,
        name: z.string(),
        status: CohortStatus,
        studentCount: z.number().int().nonnegative(),
    })),
    totalStudents: z.number().int().nonnegative(),
});
export const StudentDashboardSchema = z.object({
    programId: Uuid,
    programName: z.string(),
    organizationName: z.string(),
    cohortId: Uuid.nullable(),
    cohortName: z.string().nullable(),
    cohortStatus: CohortStatus.nullable(),
    cohortStartDate: z.string().nullable(),
    cohortEndDate: z.string().nullable(),
    enrollmentStatus: EnrollmentStatus.nullable(),
    studentIdNumber: z.string().nullable(),
});
// ─── Audit ───────────────────────────────────────────────────────────────
export const AuditEventType = z.enum([
    'COHORT_CREATED',
    'COHORT_UPDATED',
    'CLINICAL_SITE_CREATED',
    'CLINICAL_SITE_UPDATED',
    'SPECIALTY_CREATED',
    'SPECIALTY_UPDATED',
    'CLINICAL_ROLE_CREATED',
    'CLINICAL_ROLE_UPDATED',
    'PROCEDURE_CREATED',
    'PROCEDURE_UPDATED',
    'REQUIREMENT_PLAN_CREATED',
    'REQUIREMENT_PLAN_ACTIVATED',
    'REQUIREMENT_PLAN_RETIRED',
    'REQUIREMENT_PLAN_VERSION_CREATED',
    'ENROLLMENT_CREATED',
    'MEMBERSHIP_ROLE_CHANGED',
    'MEMBERSHIP_STATUS_CHANGED',
    'MEMBERSHIP_CREATED',
    'MEMBER_PROVISION_REQUESTED',
    'USER_ACCOUNT_STATUS_CHANGED',
    'PLATFORM_ADMIN_BOOTSTRAP_COMPLETED',
    'PLATFORM_ORGANIZATION_CREATED',
    'PLATFORM_ORGANIZATION_STATUS_CHANGED',
    'PLATFORM_PROGRAM_CREATED',
    'PLATFORM_PROGRAM_STATUS_CHANGED',
    'PLATFORM_PROGRAM_DIRECTOR_PROVISIONED',
    'PLATFORM_USER_STATUS_CHANGED',
    'PLATFORM_MEMBERSHIP_CREATED',
    'PLATFORM_MEMBERSHIP_STATUS_CHANGED',
    // Clinical document pipeline (Phase 2A+)
    'CLINICAL_SUBMISSION_CREATED',
    'DOCUMENT_UPLOAD_STARTED',
    'DOCUMENT_UPLOADED',
    'DOCUMENT_DUPLICATE_DETECTED',
    'DOCUMENT_PROCESSING_QUEUED',
    'DOCUMENT_PROCESSING_STARTED',
    'DOCUMENT_PROCESSING_COMPLETED',
    'DOCUMENT_PROCESSING_FAILED',
    'DOCUMENT_NORMALIZATION_STARTED',
    'DOCUMENT_NORMALIZATION_COMPLETED',
    'DOCUMENT_NORMALIZATION_FAILED',
    'DOCUMENT_QUALITY_ANALYZED',
    'DOCUMENT_TEMPLATE_IDENTIFIED',
    'DOCUMENT_TEMPLATE_UNKNOWN',
    'DOCUMENT_VIEWED',
    'DOCUMENT_DOWNLOAD_REQUESTED',
    // Document extraction (Phase 2C-A — draft interpretation layer, no academic authority)
    'DOCUMENT_EXTRACTION_QUEUED',
    'DOCUMENT_EXTRACTION_STARTED',
    'DOCUMENT_EXTRACTION_COMPLETED',
    'DOCUMENT_EXTRACTION_REVIEW_REQUIRED',
    'DOCUMENT_EXTRACTION_FAILED',
    // Future OCR/case lifecycle
    'DOCUMENT_PROCESSED',
    'STUDENT_CHANGED_FIELD',
    'STUDENT_SUBMITTED',
    'INSTRUCTOR_CHANGED_CASE',
    'CASE_APPROVED',
    'CASE_REJECTED',
    'CLARIFICATION_REQUESTED',
    'CASE_SUPERSEDED',
    'REQUIREMENT_PLAN_CHANGED',
    'USER_INVITED',
    'ROLE_CHANGED',
    'EXPORT_CREATED',
]);
export const AuditEventSchema = z.object({
    id: Uuid,
    organizationId: Uuid.nullable(),
    programId: Uuid.nullable(),
    actorUserId: Uuid.nullable(),
    eventType: z.string(),
    targetType: z.string(),
    targetId: Uuid.nullable(),
    metadata: z.record(z.string(), z.unknown()).nullable(),
    occurredAt: z.string(),
});
export const ListAuditEventsQuerySchema = PaginationQuerySchema.extend({
    programId: Uuid.optional(),
    eventType: z.string().max(60).optional(),
});
export const AuditEventListResponseSchema = z.object({
    data: z.array(AuditEventSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
});
// ─── API error envelope ──────────────────────────────────────────────────
export const ApiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.array(z.record(z.string(), z.unknown())).nullable().optional(),
    }),
});
// ─── Clinical documents (Phase 2A) ───────────────────────────────────────
export const ClinicalSubmissionStatus = z.enum([
    'DRAFT',
    'PROCESSING',
    'STUDENT_REVIEW_REQUIRED',
    'READY_FOR_INSTRUCTOR',
    'UNDER_INSTRUCTOR_REVIEW',
    'CLARIFICATION_REQUESTED',
    'APPROVED',
    'REJECTED',
    'PROCESSING_FAILED',
]);
export const DocumentType = z.enum([
    'CLINICAL_CASE_LOG',
    'CASE_EVALUATION',
    'SKILLS_CHECKOFF',
    'ATTENDANCE_RECORD',
    'REMEDIATION_FORM',
    'OTHER',
    'UNKNOWN',
]);
export const SubmissionDocumentStatus = z.enum([
    'UPLOADING',
    'STORED',
    'QUEUED',
    'PROCESSING',
    'READY',
    'FAILED',
]);
export const DocumentDerivativeType = z.enum([
    'PREVIEW_IMAGE',
    'THUMBNAIL',
    'ARCHIVAL_PDF',
    'PROCESSING_IMAGE',
    'NORMALIZED_PAGE',
]);
export const QualityDisposition = z.enum(['GOOD', 'USABLE', 'RETAKE_RECOMMENDED', 'UNUSABLE']);
export const QualityWarningCode = z.enum([
    'BLUR_DETECTED',
    'LOW_LIGHT',
    'OVEREXPOSED',
    'LOW_CONTRAST',
    'GLARE_DETECTED',
    'PAGE_EDGE_CUTOFF',
    'SEVERE_PERSPECTIVE',
    'PAGE_NOT_FOUND',
    'ORIENTATION_UNCERTAIN',
    'PAGE_BOUNDARY_UNCERTAIN',
    'LOW_RESOLUTION',
    'PAGE_TOO_SMALL',
]);
export const ExtractionReadiness = z.enum(['PENDING', 'READY_FOR_EXTRACTION', 'BLOCKED']);
/** Student-safe normalization summary — no raw CV metrics or storage keys. */
export const DocumentNormalizationSummarySchema = z.object({
    status: z.enum(['PENDING', 'PROCESSING', 'SUCCEEDED', 'PARTIAL', 'FAILED', 'NOT_APPLICABLE']),
    quality: QualityDisposition.nullable(),
    warnings: z.array(QualityWarningCode),
    warningMessages: z.array(z.string()),
    template: z.string().nullable(),
    templateUnknown: z.boolean(),
    preparedPageAvailable: z.boolean(),
    extractionReadiness: ExtractionReadiness.nullable(),
    pipelineVersion: z.number().int().positive().nullable(),
});
export const SubmissionDocumentSchema = z.object({
    id: Uuid,
    submissionId: Uuid,
    organizationId: Uuid,
    programId: Uuid,
    studentProfileId: Uuid,
    uploaderUserId: Uuid,
    documentType: DocumentType,
    originalFilename: z.string().nullable(),
    detectedMimeType: z.string(),
    originalSizeBytes: z.number().int().nonnegative(),
    sha256: z.string().length(64),
    pageCount: z.number().int().positive().nullable(),
    status: SubmissionDocumentStatus,
    duplicateOfDocumentId: Uuid.nullable(),
    failureCode: z.string().nullable(),
    failureReason: z.string().nullable(),
    storedAt: z.string().nullable(),
    processingStartedAt: z.string().nullable(),
    processingCompletedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const ClinicalSubmissionSchema = z.object({
    id: Uuid,
    organizationId: Uuid,
    programId: Uuid,
    studentProfileId: Uuid,
    cohortId: Uuid.nullable(),
    uploaderUserId: Uuid,
    documentType: DocumentType,
    status: ClinicalSubmissionStatus,
    primaryDocumentId: Uuid.nullable().optional(),
    submittedAt: z.string().nullable().optional(),
    reviewedAt: z.string().nullable().optional(),
    approvedAt: z.string().nullable().optional(),
    reviewedByUserId: Uuid.nullable().optional(),
    returnNote: z.string().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    documents: z.array(SubmissionDocumentSchema).optional(),
    /** Present when API includes normalization summaries per document. */
    normalizationByDocumentId: z
        .record(z.string(), DocumentNormalizationSummarySchema)
        .optional(),
});
// ─── Clinical submission workflow (Phase 1 product) ───────────────────────
export const SheetRowOccupancy = z.enum(['EMPTY', 'OCCUPIED', 'UNCERTAIN']);
export const SheetSignaturePresence = z.enum(['PRESENT', 'MISSING', 'UNCERTAIN']);
export const SheetCaseRole = z.enum(['FIRST_SCRUB', 'SECOND_SCRUB', 'OBSERVATION']);
export const FacultyCaseDecisionType = z.enum(['APPROVE', 'EXCLUDE']);
export const AcademicLedgerEntryStatus = z.enum(['ACTIVE', 'VOIDED']);
export const FacultyCaseDecisionSchema = z.object({
    id: Uuid,
    submissionRowId: Uuid,
    decision: FacultyCaseDecisionType,
    facultyProcedure: z.string().nullable(),
    facultyCaseDate: z.string().nullable(),
    facultySpecialty: z.string().nullable(),
    facultyRole: SheetCaseRole.nullable(),
    reviewedInterpretationVersion: z.number().int().positive(),
    reviewedInterpretationSha256: z.string().length(64),
    reviewNote: z.string().nullable(),
    decidedByUserId: Uuid,
    decidedAt: z.string(),
    supersededAt: z.string().nullable(),
});
export const SheetCrossedOutState = z.enum(['PRESENT', 'ABSENT', 'UNCERTAIN']);
export const SheetSourceProvenance = z.enum([
    'MANUAL_PENDING',
    'STUDENT_ENTERED',
    'AUTOMATED',
    'AUTOMATED_STUDENT_CONFIRMED',
]);
export const ClinicalRowReviewStatus = z.enum([
    'AUTO_ACCEPT',
    'STUDENT_REVIEW_REQUIRED',
    'INSTRUCTOR_REVIEW_REQUIRED',
]);
export const ClinicalSheetRowInterpretationSchema = z.object({
    id: Uuid,
    submissionId: Uuid,
    rowIndex: z.number().int().min(1).max(10),
    sourceOccupancyState: SheetRowOccupancy,
    sourceCrossedOutObserved: z.boolean(),
    sourceCrossedOutState: SheetCrossedOutState,
    sourceSignaturePresence: SheetSignaturePresence,
    sourceProvenance: SheetSourceProvenance,
    studentOccupancyResolution: SheetRowOccupancy.nullable(),
    studentSignatureResolution: SheetSignaturePresence.nullable(),
    studentCrossedOutAcknowledged: z.boolean(),
    studentCrossedOutResolution: SheetCrossedOutState.nullable(),
    procedureTextRaw: z.string().nullable(),
    procedureTextNormalized: z.string().nullable(),
    caseDateRaw: z.string().nullable(),
    caseDateNormalized: z.string().nullable(),
    specialtyRaw: z.string().nullable(),
    specialtyNormalized: z.string().nullable(),
    caseRole: SheetCaseRole.nullable(),
    automatedProcedureRaw: z.string().nullable().optional(),
    automatedProcedureNormalized: z.string().nullable().optional(),
    automatedCaseDateRaw: z.string().nullable().optional(),
    automatedCaseDateNormalized: z.string().nullable().optional(),
    automatedSpecialtyRaw: z.string().nullable().optional(),
    automatedSpecialtyNormalized: z.string().nullable().optional(),
    automatedCaseRole: SheetCaseRole.nullable().optional(),
    interpretationVersion: z.number().int().positive(),
    reviewRequired: z.boolean(),
    reviewStatus: ClinicalRowReviewStatus,
    reviewReasons: z.array(z.string()),
    confidence: z.number().int().min(0).max(100).nullable(),
    studentConfirmedAt: z.string().nullable(),
    studentEditedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    facultyDecision: FacultyCaseDecisionSchema.nullable().optional(),
    decisionStale: z.boolean().optional(),
});
export const SourceEvidenceSummarySchema = z.object({
    id: Uuid,
    originalFilename: z.string().nullable(),
    detectedMimeType: z.string(),
    originalSizeBytes: z.number().int().nonnegative(),
    sha256: z.string().length(64),
    status: SubmissionDocumentStatus,
    createdAt: z.string(),
});
export const ClinicalSubmissionDetailSchema = ClinicalSubmissionSchema.extend({
    rows: z.array(ClinicalSheetRowInterpretationSchema),
    reviewNeededCount: z.number().int().nonnegative(),
    dispositionComplete: z.boolean(),
    sourceEvidence: SourceEvidenceSummarySchema.nullable(),
});
export const FacultyQueueSubmissionSchema = ClinicalSubmissionDetailSchema.extend({
    studentName: z.string(),
});
export const UpdateRowInterpretationInputSchema = z
    .object({
    studentOccupancyResolution: SheetRowOccupancy.optional(),
    studentSignatureResolution: SheetSignaturePresence.optional(),
    studentCrossedOutAcknowledged: z.boolean().optional(),
    studentCrossedOutResolution: SheetCrossedOutState.optional(),
    procedureText: z.string().trim().max(500).optional(),
    caseDate: CalendarIsoDate.optional(),
    specialty: z.string().trim().max(200).optional(),
    caseRole: SheetCaseRole.optional(),
    confirmRow: z.boolean().optional(),
})
    .strict();
export const FacultyCaseDecisionInputSchema = z
    .object({
    decision: FacultyCaseDecisionType,
    facultyProcedure: z.string().trim().max(500).optional(),
    facultyCaseDate: CalendarIsoDate.optional(),
    facultySpecialty: z.string().trim().max(200).optional(),
    facultyRole: SheetCaseRole.optional(),
    reviewNote: z.string().trim().max(1000).optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.decision !== 'APPROVE')
        return;
    if (!value.facultyProcedure?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'facultyProcedure is required', path: ['facultyProcedure'] });
    }
    if (!value.facultyCaseDate) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'facultyCaseDate is required', path: ['facultyCaseDate'] });
    }
    if (!value.facultySpecialty?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'facultySpecialty is required', path: ['facultySpecialty'] });
    }
    if (!value.facultyRole) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'facultyRole is required', path: ['facultyRole'] });
    }
});
export const ReturnSubmissionInputSchema = z
    .object({
    returnNote: z.string().trim().min(1, 'Return note is required').max(2000),
})
    .strict();
export const AcademicLedgerEntrySchema = z.object({
    id: Uuid,
    submissionId: Uuid,
    submissionRowId: Uuid,
    sourceDocumentId: Uuid,
    sourceDocumentSha256: z.string().length(64),
    procedure: z.string(),
    caseDate: z.string(),
    specialty: z.string(),
    caseRole: SheetCaseRole,
    approvedByUserId: Uuid,
    approvedAt: z.string(),
    status: AcademicLedgerEntryStatus,
    createdAt: z.string(),
});
export const LedgerSummarySchema = z.object({
    totalApprovedCases: z.number().int().nonnegative(),
    countsBySpecialty: z.record(z.string(), z.number().int().nonnegative()),
    countsByRole: z.record(z.string(), z.number().int().nonnegative()),
});
export const RequirementProgressDetailsSchema = z.object({
    secondScrubContributing: z.number().int().nonnegative().optional(),
    secondScrubCredited: z.number().int().nonnegative().optional(),
    qualifyingSpecialtyCount: z.number().int().nonnegative().optional(),
    perSpecialtyQualifyingCounts: z.record(z.string(), z.number().int().nonnegative()).optional(),
});
export const RequirementProgressItemSchema = z.object({
    ruleId: Uuid,
    ruleType: z.string(),
    threshold: z.number().int().nullable(),
    current: z.number().int().nonnegative(),
    met: z.boolean().nullable(),
    remaining: z.number().int().nullable(),
    evaluation: z.enum(['EVALUATED', 'UNEVAULABLE_RULE_TYPE', 'UNEVAULABLE_INVALID_CONFIG']),
    details: RequirementProgressDetailsSchema.optional(),
});
export const RequirementProgressResponseSchema = z.object({
    progress: z.array(RequirementProgressItemSchema),
    totalApprovedCases: z.number().int().nonnegative(),
    reason: z.enum(['NO_ACTIVE_REQUIREMENT_PLAN']).optional(),
});
export const UploadClinicalDocumentInputSchema = z
    .object({
    documentType: DocumentType.default('UNKNOWN'),
})
    .strict();
export const ListClinicalSubmissionsQuerySchema = PaginationQuerySchema.extend({
    studentProfileId: Uuid.optional(),
    status: ClinicalSubmissionStatus.optional(),
});
export const ClinicalSubmissionListResponseSchema = z.object({
    data: z.array(ClinicalSubmissionSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
});
export const DocumentUploadResponseSchema = z.object({
    submission: ClinicalSubmissionSchema,
    document: SubmissionDocumentSchema,
    exactDuplicate: z.boolean(),
    duplicateOfDocumentId: Uuid.nullable(),
    reviewPath: z.string().optional(),
    idempotentReplay: z.boolean().optional(),
});
export const DocumentContentUrlResponseSchema = z.object({
    url: z.string().nullable(),
    /** When url is null, client should use the authenticated stream endpoint. */
    streamPath: z.string(),
    expiresAt: z.string().nullable(),
});
// ─── Document extraction (Phase 2C-A) ─────────────────────────────────────
//
// AI/OCR output is NOT academic truth — every type here describes a DRAFT
// interpretation layer. Nothing in this section may imply faculty approval
// or requirement progress. See docs/DOCUMENT_EXTRACTION.md.
export const DocumentExtractionRunStatus = z.enum(['QUEUED', 'PROCESSING', 'SUCCEEDED', 'FAILED', 'REVIEW_REQUIRED']);
export const ExtractionRowOccupancy = z.enum(['EMPTY', 'OCCUPIED', 'UNCERTAIN']);
/** Draft interpretation review state — deliberately distinct from academic (faculty) approval. */
export const ExtractionReviewStatus = z.enum([
    'UNREVIEWED',
    'LOW_CONFIDENCE',
    'NEEDS_CONFIRMATION',
    'CONFIRMED_INTERPRETATION',
    'REJECTED_INTERPRETATION',
]);
export const ExtractedFieldType = z.enum([
    'DATE',
    'PROCEDURE',
    'SURGEON',
    'GENERAL_SPECIALTY_MARK',
    'SPECIALTY_CODE',
    'FIRST_SCRUB_MARK',
    'SECOND_SCRUB_MARK',
    'OBSERVATION_MARK',
    'PRECEPTOR_SIGNATURE_PRESENCE',
    'STUDENT_SIGNATURE_PRESENCE',
    'OTHER',
]);
export const ExtractionWarningCode = z.enum([
    'LOW_CONFIDENCE',
    'ILLEGIBLE',
    'MULTIPLE_VALUES',
    'OUTSIDE_EXPECTED_REGION',
    'INVALID_DATE_FORMAT',
    'AMBIGUOUS_MARK',
    'POSSIBLE_STRIKEOUT',
    'POSSIBLE_DUPLICATE',
    'SIGNATURE_UNCERTAIN',
]);
export const NormalizedRectSchema = z.object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().min(0),
    height: z.number().min(0),
});
/** Student/instructor-safe run summary — never raw provider payloads or storage keys. */
export const DocumentExtractionRunSummarySchema = z.object({
    id: Uuid,
    status: DocumentExtractionRunStatus,
    provider: z.string(),
    providerModel: z.string(),
    extractionPipelineVersion: z.number().int().positive(),
    failureCode: z.string().nullable(),
    safeFailureMessage: z.string().nullable(),
    startedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    createdAt: z.string(),
    rowCount: z.number().int().nonnegative(),
    fieldCount: z.number().int().nonnegative(),
    needsReviewCount: z.number().int().nonnegative(),
});
export const ExtractedRowSchema = z.object({
    id: Uuid,
    rowIndex: z.number().int().positive(),
    sourceBbox: NormalizedRectSchema,
    occupancy: ExtractionRowOccupancy,
    confidence: z.number().min(0).max(1).nullable(),
    reviewStatus: ExtractionReviewStatus,
    warnings: z.array(ExtractionWarningCode),
});
export const ExtractedFieldSchema = z.object({
    id: Uuid,
    rowIndex: z.number().int().positive().nullable(),
    fieldType: ExtractedFieldType,
    rawText: z.string().nullable(),
    normalizedValue: z.string().nullable(),
    providerConfidence: z.number().min(0).max(1).nullable(),
    applicationConfidence: z.number().min(0).max(1).nullable(),
    sourceRegion: z.string().nullable(),
    sourceBbox: NormalizedRectSchema,
    templateRegionKey: z.string().nullable(),
    reviewStatus: ExtractionReviewStatus,
    warnings: z.array(ExtractionWarningCode),
});
/** Draft interpretation for a document — GET /api/documents/:id/extraction. */
export const DocumentExtractionResponseSchema = z.object({
    run: DocumentExtractionRunSummarySchema.nullable(),
    rows: z.array(ExtractedRowSchema),
    fields: z.array(ExtractedFieldSchema),
});
export const QueueExtractionResponseSchema = z.object({
    status: z.enum(['QUEUED', 'ALREADY_QUEUED']),
});
// ─── Route paths (single source of truth) ────────────────────────────────
export const routes = {
    health: '/api/health',
    healthLive: '/api/health/live',
    healthReady: '/api/health/ready',
    healthWorkers: '/api/health/workers',
    healthAlerts: '/api/health/alerts',
    auth: '/api/auth',
    login: '/api/auth/login',
    logout: '/api/auth/logout',
    publicDemoRequests: '/api/public/demo-requests',
    me: '/api/auth/me',
    tenantContexts: '/api/auth/tenant-contexts',
    tenantContext: '/api/auth/tenant-context',
    programs: '/api/programs',
    program: (id) => `/api/programs/${id}`,
    dashboards: '/api/dashboards',
    pdDashboard: '/api/dashboards/program-director',
    instructorDashboard: '/api/dashboards/instructor',
    studentDashboard: '/api/dashboards/student',
    cohorts: '/api/cohorts',
    cohort: (id) => `/api/cohorts/${id}`,
    clinicalSites: '/api/clinical-sites',
    clinicalSite: (id) => `/api/clinical-sites/${id}`,
    specialties: '/api/specialties',
    specialty: (id) => `/api/specialties/${id}`,
    specialtyAliases: (specialtyId) => `/api/specialties/${specialtyId}/aliases`,
    specialtyAlias: (specialtyId, id) => `/api/specialties/${specialtyId}/aliases/${id}`,
    clinicalRoles: '/api/clinical-roles',
    clinicalRole: (id) => `/api/clinical-roles/${id}`,
    procedures: '/api/procedures',
    procedure: (id) => `/api/procedures/${id}`,
    procedureAliases: (procedureId) => `/api/procedures/${procedureId}/aliases`,
    procedureAlias: (procedureId, id) => `/api/procedures/${procedureId}/aliases/${id}`,
    requirementPlans: '/api/requirement-plans',
    requirementTemplates: '/api/requirement-templates',
    requirementPlan: (id) => `/api/requirement-plans/${id}`,
    requirementPlanVersions: (planId) => `/api/requirement-plans/${planId}/versions`,
    requirementPlanActivate: (planId) => `/api/requirement-plans/${planId}/activate`,
    requirementPlanRules: (planId) => `/api/requirement-plans/${planId}/rules`,
    requirementPlanAssign: (planId) => `/api/requirement-plans/${planId}/assign`,
    students: '/api/students',
    student: (id) => `/api/students/${id}`,
    studentEnrollments: (studentId) => `/api/students/${studentId}/enrollments`,
    members: '/api/members',
    member: (id) => `/api/members/${id}`,
    auditEvents: '/api/audit-events',
    clinicalSubmissions: '/api/clinical-submissions',
    clinicalSubmission: (id) => `/api/clinical-submissions/${id}`,
    clinicalSubmissionWorkflowList: '/api/clinical-submission-workflow',
    clinicalSubmissionWorkflowDemo: '/api/clinical-submission-workflow/demo',
    clinicalSubmissionWorkflowDetail: (id) => `/api/clinical-submission-workflow/${id}`,
    clinicalSubmissionWorkflowRow: (id, rowId) => `/api/clinical-submission-workflow/${id}/rows/${rowId}`,
    clinicalSubmissionWorkflowSubmit: (id) => `/api/clinical-submission-workflow/${id}/submit`,
    clinicalSubmissionWorkflowBeginReview: (id) => `/api/clinical-submission-workflow/${id}/begin-review`,
    clinicalSubmissionWorkflowFacultyDecision: (id, rowId) => `/api/clinical-submission-workflow/${id}/rows/${rowId}/faculty-decision`,
    clinicalSubmissionWorkflowApprove: (id) => `/api/clinical-submission-workflow/${id}/approve`,
    clinicalSubmissionWorkflowReturn: (id) => `/api/clinical-submission-workflow/${id}/return`,
    academicLedger: '/api/academic-ledger',
    academicLedgerSummary: '/api/academic-ledger/summary',
    clinicalSubmissionWorkflowRequirementProgress: '/api/clinical-submission-workflow/requirement-progress',
    documents: '/api/documents',
    document: (id) => `/api/documents/${id}`,
    documentContent: (id) => `/api/documents/${id}/content`,
    documentContentUrl: (id) => `/api/documents/${id}/content-url`,
    documentRetryProcessing: (id) => `/api/documents/${id}/retry-processing`,
    documentNormalization: (id) => `/api/documents/${id}/normalization`,
    documentPreparedPageUrl: (id) => `/api/documents/${id}/prepared-page-url`,
    documentPreparedPage: (id) => `/api/documents/${id}/prepared-page`,
    documentNormalizationDebug: (id) => `/api/documents/${id}/normalization-debug`,
    documentExtraction: (id) => `/api/documents/${id}/extraction`,
    studentDocuments: (studentId) => `/api/students/${studentId}/documents`,
    platformOverview: '/api/platform/overview',
    platformOrganizations: '/api/platform/organizations',
    platformOrganization: (organizationId) => `/api/platform/organizations/${organizationId}`,
    platformOrganizationStatus: (organizationId) => `/api/platform/organizations/${organizationId}/status`,
    platformOrganizationPrograms: (organizationId) => `/api/platform/organizations/${organizationId}/programs`,
    platformProgram: (programId) => `/api/platform/programs/${programId}`,
    platformProgramStatus: (programId) => `/api/platform/programs/${programId}/status`,
    platformProgramDirectors: (programId) => `/api/platform/programs/${programId}/program-directors`,
    platformProgramMemberships: (programId) => `/api/platform/programs/${programId}/memberships`,
    platformUsers: '/api/platform/users',
    platformUser: (userId) => `/api/platform/users/${userId}`,
    platformUserStatus: (userId) => `/api/platform/users/${userId}/status`,
    platformUserMemberships: (userId) => `/api/platform/users/${userId}/memberships`,
};
/** User-facing messages for quality warning codes. */
export const QUALITY_WARNING_MESSAGES = {
    BLUR_DETECTED: 'Image is blurry',
    LOW_LIGHT: 'Photo may be too dark',
    OVEREXPOSED: 'Photo may be overexposed',
    LOW_CONTRAST: 'Low contrast — handwriting may be hard to read',
    GLARE_DETECTED: 'Glare may obscure handwriting',
    PAGE_EDGE_CUTOFF: 'An edge of the page may be cut off',
    SEVERE_PERSPECTIVE: 'Strong camera angle detected',
    PAGE_NOT_FOUND: 'Could not locate the page in the photo',
    ORIENTATION_UNCERTAIN: 'Page orientation could not be confirmed',
    PAGE_BOUNDARY_UNCERTAIN: 'Page edges could not be confirmed confidently',
    LOW_RESOLUTION: 'Resolution may be too low for reliable processing',
    PAGE_TOO_SMALL: 'Page occupies too little of the photo',
};
